import { useState } from 'react';
import { Outlet, useParams, useMatch } from 'react-router-dom';
import { Plus, ChevronDown, FolderPlus, PencilRuler, FileUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useListFolders, useFolderManagement } from '@/hooks';
import useFolderBrowserParams from '@/hooks/useFolderBrowserParams';
import FolderBreadcrumbs from '@/components/FolderBrowserLayout/FolderBreadcrumbs';
import CreateFolderModal from '../workflows/CreateFolderModal';
import RenameFolderModal from '../workflows/RenameFolderModal';
import { RenderIf } from '@/components/RenderIf';
import type { PromptsProps } from './types';
import {
    PromptsContainer,
    PromptsContent,
    PromptsHeader,
    HeaderSpacer,
    ContentContainer,
} from './styled';

function Prompts(_props: PromptsProps) {
    const { agentId } = useParams();
    const [shouldOpenCreateModal, setShouldOpenCreateModal] = useState(false);
    const [shouldOpenImportModal, setShouldOpenImportModal] = useState(false);

    // Check if we're on the prompts list page (not the editor)
    const isOnListPage = useMatch('/agents/:agentId/prompts');

    // Folder browser params (URL-based state)
    const { currentPath, setCurrentPath } = useFolderBrowserParams();

    // Fetch folders
    const { folders, isLoading: isLoadingFolders } = useListFolders({
        agentId: agentId!,
        entityType: 'prompt',
    });

    // Folder management
    const {
        isCreateFolderModalOpen,
        createFolderParentPath,
        renameFolderState,
        handleCreateFolder,
        handleCreateSubfolder,
        handleCloseCreateFolder,
        handleRenameFolder,
        handleCloseRenameFolder,
        handleDeleteFolder,
    } = useFolderManagement({
        agentId: agentId!,
        entityType: 'prompt',
        currentPath,
        setCurrentPath,
    });

    const handleCreatePrompt = () => {
        setShouldOpenCreateModal(true);
    };

    const handleImportPrompt = () => {
        setShouldOpenImportModal(true);
    };

    return (
        <PromptsContainer>
            <PromptsContent>
                <RenderIf isTrue={!!isOnListPage}>
                    <PromptsHeader>
                        <FolderBreadcrumbs currentPath={currentPath} onNavigate={setCurrentPath} />

                        <HeaderSpacer />

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button className="gap-2">
                                    <Plus size={16} />
                                    New
                                    <ChevronDown size={14} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleCreatePrompt}>
                                    <PencilRuler size={16} />
                                    New Prompt
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleImportPrompt}>
                                    <FileUp size={16} />
                                    Import Prompt
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleCreateFolder}>
                                    <FolderPlus size={16} />
                                    New Folder
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </PromptsHeader>
                </RenderIf>

                <ContentContainer>
                    <Outlet
                        context={{
                            shouldOpenCreateModal,
                            setShouldOpenCreateModal,
                            shouldOpenImportModal,
                            setShouldOpenImportModal,
                            currentPath,
                            setCurrentPath,
                            onCreateFolder: handleCreateFolder,
                            onCreateSubfolder: handleCreateSubfolder,
                            onRenameFolder: handleRenameFolder,
                            onDeleteFolder: handleDeleteFolder,
                            folders,
                            isLoadingFolders,
                        }}
                    />
                </ContentContainer>
            </PromptsContent>

            <CreateFolderModal
                isOpen={isCreateFolderModalOpen}
                onClose={handleCloseCreateFolder}
                agentId={agentId!}
                entityType="prompt"
                currentPath={createFolderParentPath}
            />

            <RenameFolderModal
                isOpen={renameFolderState.isOpen}
                onClose={handleCloseRenameFolder}
                folderId={renameFolderState.folderId}
                folderPath={renameFolderState.folderPath}
                currentName={renameFolderState.folderName}
                currentPath={currentPath}
                setCurrentPath={setCurrentPath}
                agentId={agentId!}
                entityType="prompt"
            />
        </PromptsContainer>
    );
}

export default Prompts;
